import { useContext, useEffect, useRef, useState } from 'react';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

/**
 * The page's dependency links with their bar dates, `{ links, bars }`, while `enabled`; null
 * otherwise, and nothing is fetched.
 *
 * Links depend only on the page's plans, so they are refetched after an edit (a new links
 * revision) and not on period, filter or search changes. The edit's refetch starts alongside the
 * gantt one, and the result is only returned once the gantt rows on screen come from the same
 * revision, so arrows from before an edit are never drawn over the rows after it.
 */
export default function useGanttLinks(enabled) {
    const { ganttChart, fetchProjectGanttChartLinksUrl, linksRevision, projectSourceType, projectSourceId } = useContext(ProjectPlanContext);
    const [fetched, setFetched] = useState(null);
    const generationRef = useRef(0);

    useEffect(() => {
        const generation = ++generationRef.current;

        if (!enabled || !fetchProjectGanttChartLinksUrl) {
            setFetched(null);

            return undefined;
        }

        axios
            .get(fetchProjectGanttChartLinksUrl, { params: { source_type: projectSourceType, source_id: projectSourceId } })
            .then((response) => {
                if (generation === generationRef.current) {
                    // The previous revision is kept so the rows still on screen keep their arrows until the
                    // gantt refetch for this revision lands.
                    setFetched((prev) => ({ latest: { revision: linksRevision, data: response.data }, previous: prev?.latest ?? null }));
                }
            })
            .catch((error) => {
                console.warn('[useGanttLinks] Failed to load dependency links', error);

                // Without an entry for this revision every arrow would stay hidden until the next
                // edit, so the last good links stand in for it.
                if (generation === generationRef.current) {
                    setFetched((prev) => ({ latest: { revision: linksRevision, data: prev?.latest?.data ?? null }, previous: prev?.latest ?? null }));
                }
            });

        return () => {
            generationRef.current += 1;
        };
    }, [enabled, fetchProjectGanttChartLinksUrl, linksRevision, projectSourceType, projectSourceId]);

    if (!enabled || !fetched || !ganttChart.data) {
        return null;
    }

    const match = [fetched.latest, fetched.previous].find((entry) => entry?.revision === ganttChart.data.linksRevision);

    return match ? match.data : null;
}
